import {
  Routes,
  type AdminOverviewResponse,
  type AgentInstallResponse,
  type AuthResponse,
  type AuthSignupRequest,
  type DiffResponse,
  type Environment,
  type FailureBundleResponse,
  type FlakyResponse,
  type GeneratedPlan,
  type HealthResponse,
  type ProjectCreateRequest,
  type ProjectPatchRequest,
  type RunAcceptedResponse,
  type RunResponse,
  type RunWaitResponse,
  type ScheduleCreateRequest,
  type SchedulePatchRequest,
  type TestCreateRequest,
  type TestPatchRequest,
  type TestResultResponse,
  type TestlistCreateRequest,
  type TestlistRunResponse,
  type ValidationIssue,
  type WhoAmIResponse,
  type PublicSmtpConfig,
  type SmtpConfig,
} from '@verifyloop/core';
import { api, request, requestText } from './client';
import type {
  AdminLlmResponse,
  AgentsResponse,
  ApiKeyCreateResponse,
  ApiKeyListResponse,
  BillingCheckoutRequest,
  BillingCheckoutResponse,
  BillingSubscriptionResponse,
  EnvironmentListResponse,
  ProjectListResponse,
  ProjectSummary,
  RunListResponse,
  ScheduleListResponse,
  ScheduleRunsResponse,
  ScheduleSummary,
  SetAdminLlmRequest,
  TestListListResponse,
  TestListSummary,
  TestSummary,
  TestsResponse,
  TunnelSummary,
  UsageResponse,
} from './types';

/**
 * One function per thing the dashboard does, so a page never assembles a URL
 * by hand and never guesses an envelope. Route strings come from `Routes`: the
 * same object the server mounts, so a rename breaks the build here first.
 */

const NO_AUTH = { auth: false } as const;

// --------------------------------------------------------------------- auth

export const health = (): Promise<HealthResponse> =>
  request<HealthResponse>(Routes.health, { ...NO_AUTH });

export const signUp = (body: AuthSignupRequest): Promise<AuthResponse> =>
  api.post<AuthResponse>(Routes.authSignup, body, { ...NO_AUTH });

export const signIn = (body: AuthSignupRequest): Promise<AuthResponse> =>
  api.post<AuthResponse>(Routes.authSignin, body, { ...NO_AUTH });

export const whoAmI = (): Promise<WhoAmIResponse & { user: { role?: 'member' | 'admin' } }> =>
  api.get<WhoAmIResponse & { user: { role?: 'member' | 'admin' } }>(Routes.authWhoami);

export const listApiKeys = (): Promise<ApiKeyListResponse> =>
  api.get<ApiKeyListResponse>(Routes.authKeys);

export const createApiKey = (label: string, scopes: string[]): Promise<ApiKeyCreateResponse> =>
  api.post<ApiKeyCreateResponse>(Routes.authKeys, { label, scopes });

export const revokeApiKey = (id: string): Promise<void> => api.del<void>(Routes.authKeysRevoke(id));

export const usage = (): Promise<UsageResponse> => api.get<UsageResponse>(Routes.usage);

// ----------------------------------------------------------------- projects

export const listProjects = (): Promise<ProjectListResponse> =>
  api.get<ProjectListResponse>(Routes.projects);

export const getProject = (id: string): Promise<{ project: ProjectSummary }> =>
  api.get<{ project: ProjectSummary }>(Routes.project(id));

export const createProject = (body: ProjectCreateRequest): Promise<{ project: ProjectSummary }> =>
  api.post<{ project: ProjectSummary }>(Routes.projects, body);

export const patchProject = (
  id: string,
  body: ProjectPatchRequest,
): Promise<{ project: ProjectSummary }> =>
  api.patch<{ project: ProjectSummary }>(Routes.project(id), body);

export const deleteProject = (id: string): Promise<void> => api.del<void>(Routes.project(id));

// ------------------------------------------------------------ environments

export const listEnvironments = (projectId: string): Promise<EnvironmentListResponse> =>
  api.get<EnvironmentListResponse>(Routes.projectEnvironments(projectId));

export const createEnvironment = (
  projectId: string,
  body: {
    name: string;
    url?: string;
    credentials?: Environment['credentials'];
    isDefault?: boolean;
  },
): Promise<{ environment: Environment }> =>
  api.post<{ environment: Environment }>(Routes.projectEnvironments(projectId), body);

export const setDefaultEnvironment = (
  projectId: string,
  environmentId: string,
): Promise<{ environment: Environment }> =>
  api.post<{ environment: Environment }>(Routes.projectSetDefaultEnv(projectId, environmentId));

export const deleteEnvironment = (id: string): Promise<void> =>
  api.del<void>(Routes.environment(id));

// ------------------------------------------------------------------- tests

export const listTests = (projectId?: string): Promise<TestsResponse> =>
  api.get<TestsResponse>(Routes.tests, projectId ? { query: { project: projectId } } : {});

export const getTest = (id: string): Promise<{ test: TestSummary }> =>
  api.get<{ test: TestSummary }>(Routes.test(id));

export const createTest = (
  body: TestCreateRequest,
): Promise<{ test: TestSummary; warnings: ValidationIssue[] }> =>
  api.post<{ test: TestSummary; warnings: ValidationIssue[] }>(Routes.tests, body);

export const patchTest = (id: string, body: TestPatchRequest): Promise<{ test: TestSummary }> =>
  api.patch<{ test: TestSummary }>(Routes.test(id), body);

/** Replacing the plan wholesale is `PUT /v1/tests/:id/plan`, not PATCH. */
export const replacePlan = (
  id: string,
  planSteps: TestCreateRequest['planSteps'],
  healed?: boolean,
): Promise<{ test: TestSummary }> =>
  api.put<{ test: TestSummary }>(Routes.testPlan(id), { planSteps, ...(healed !== undefined ? { healed } : {}) });

export const deleteTest = (id: string): Promise<void> => api.del<void>(Routes.test(id));

export const testCode = (id: string): Promise<string> => requestText(Routes.testCode(id));

export const testResult = (id: string): Promise<TestResultResponse> =>
  api.get<TestResultResponse>(Routes.testResult(id));

export const testFailure = (testId: string, runId?: string): Promise<FailureBundleResponse> =>
  api.get<FailureBundleResponse>(Routes.testFailure(testId), { query: runId ? { run: runId } : {} });

export const testDiff = (testId: string, from: string, to: string): Promise<DiffResponse> =>
  api.get<DiffResponse>(Routes.testDiff(testId), { query: { from, to } });

export interface RerunResult {
  runIds: string[];
  creditsCharged: number;
  skipped: { testId: string; reason: string }[];
}

export const rerunTest = (
  id: string,
  body: { wait?: boolean; timeoutMs?: number; recordVideo?: boolean; autoHeal?: boolean } = {},
): Promise<RerunResult> => api.post<RerunResult>(Routes.testRerun(id), body);

export const flakyCheck = (id: string, repeats = 3): Promise<FlakyResponse> =>
  api.post<FlakyResponse>(Routes.testFlaky(id), { repeats, timeoutMs: 600_000 });

// -------------------------------------------------------------------- runs

export const listRuns = (query: { project?: string; test?: string; limit?: number }) =>
  api.get<RunListResponse>(Routes.runs, { query });

export const startRun = (body: {
  testIds?: string[];
  projectId?: string;
  environmentId?: string;
  tunnelClientId?: string;
  trigger?: string;
  recordVideo?: boolean;
  autoHeal?: boolean;
}): Promise<RunAcceptedResponse> => api.post<RunAcceptedResponse>(Routes.runs, body);

export const waitForRuns = (runIds: string[], timeoutMs = 300_000): Promise<RunWaitResponse> =>
  api.post<RunWaitResponse>(Routes.runWait, { runIds, timeoutMs });

export const getRun = (runId: string): Promise<RunResponse> =>
  api.get<RunResponse>(Routes.run(runId));

export const cancelRun = (runId: string): Promise<{ run: unknown; refunded: boolean }> =>
  api.post<{ run: unknown; refunded: boolean }>(Routes.runCancel(runId));

// --------------------------------------------------------------- test lists

export const listTestLists = (): Promise<TestListListResponse> =>
  api.get<TestListListResponse>(Routes.testlists);

export const createTestList = (
  body: TestlistCreateRequest,
): Promise<{ testlist: TestListSummary }> =>
  api.post<{ testlist: TestListSummary }>(Routes.testlists, body);

export const deleteTestList = (id: string): Promise<void> => api.del<void>(Routes.testlist(id));

export const runTestList = (
  id: string,
  body: { caseIds?: string[]; wait?: boolean; timeoutMs?: number } = {},
): Promise<TestlistRunResponse & { creditsCharged?: number }> =>
  api.post<TestlistRunResponse & { creditsCharged?: number }>(Routes.testlistRun(id), body);

// ---------------------------------------------------------------- schedules

export const createSchedule = (body: ScheduleCreateRequest): Promise<{ schedule: ScheduleSummary }> =>
  api.post<{ schedule: ScheduleSummary }>(Routes.schedules, body);

export const listSchedules = (): Promise<ScheduleListResponse> =>
  api.get<ScheduleListResponse>(Routes.schedules);

export const patchSchedule = (id: string, body: SchedulePatchRequest): Promise<{ schedule: ScheduleSummary }> =>
  api.patch<{ schedule: ScheduleSummary }>(Routes.schedule(id), body);

export const pauseSchedule = (id: string): Promise<{ schedule: ScheduleSummary }> =>
  api.post<{ schedule: ScheduleSummary }>(Routes.schedulePause(id));

export const resumeSchedule = (id: string): Promise<{ schedule: ScheduleSummary }> =>
  api.post<{ schedule: ScheduleSummary }>(Routes.scheduleResume(id));

export const deleteSchedule = (id: string): Promise<void> => api.del<void>(Routes.schedule(id));

export const scheduleRuns = (id: string): Promise<ScheduleRunsResponse> =>
  api.get<ScheduleRunsResponse>(Routes.scheduleRuns(id));

// ------------------------------------------------------------------ tunnels

export const listTunnels = (): Promise<{ tunnels: TunnelSummary[] }> =>
  api.get<{ tunnels: TunnelSummary[] }>(Routes.tunnels);

export const stopTunnel = (clientId: string): Promise<void> =>
  api.del<void>(Routes.tunnel(clientId));

export const stopAllTunnels = (): Promise<{ stopped: string[] }> =>
  api.post<{ stopped: string[] }>(Routes.tunnelStopAll, { confirm: true });

// ------------------------------------------------------------------- agents

export const listAgents = (): Promise<AgentsResponse> => api.get<AgentsResponse>(Routes.agents);

export const agentSkill = (name = 'default'): Promise<string> => requestText(Routes.skill(name));

export const installAgentSkill = (
  agents: string[],
  scope: 'user' | 'project',
): Promise<AgentInstallResponse> =>
  api.post<AgentInstallResponse>(Routes.agentsInstall, { agents, scope });

// ----------------------------------------------------------------- artifacts

export const artifactPath = (runId: string, artifactId: string): string =>
  Routes.runArtifact(runId, artifactId);

export const runArchivePath = (runId: string): string => Routes.runArchive(runId);

// ----------------------------------------------------------------- admin / owner

export const getAdminOverview = (): Promise<AdminOverviewResponse> =>
  api.get<AdminOverviewResponse>(Routes.adminOverview);

export const getAdminLlm = (): Promise<AdminLlmResponse> =>
  api.get<AdminLlmResponse>(Routes.adminLlm);

export const setAdminLlm = (body: SetAdminLlmRequest): Promise<AdminLlmResponse> =>
  api.put<AdminLlmResponse>(Routes.adminLlm, body);

export const clearAdminLlm = (): Promise<AdminLlmResponse> =>
  api.del<AdminLlmResponse>(Routes.adminLlm);

export const getAdminLlmModels = (body: {
  provider: string;
  baseUrl?: string;
  apiKey?: string;
}): Promise<{ models: Array<{ id: string; label: string }> }> =>
  api.post<{ models: Array<{ id: string; label: string }> }>(Routes.adminLlmModels, body);

export const getAdminSmtp = (): Promise<{ smtp: PublicSmtpConfig }> =>
  api.get<{ smtp: PublicSmtpConfig }>(Routes.adminSmtp);

export const setAdminSmtp = (body: Partial<SmtpConfig>): Promise<{ smtp: PublicSmtpConfig }> =>
  api.put<{ smtp: PublicSmtpConfig }>(Routes.adminSmtp, body);

export const clearAdminSmtp = (): Promise<{ smtp: PublicSmtpConfig }> =>
  api.del<{ smtp: PublicSmtpConfig }>(Routes.adminSmtp);

export const testAdminSmtp = (body: { host?: string; port?: number; secure?: boolean }): Promise<{ ok: boolean; message: string }> =>
  api.post<{ ok: boolean; message: string }>(Routes.adminSmtpTest, body);

export interface AdminOrganizationItem {
  id: string;
  name: string;
  createdAt: string;
  ownerEmail: string;
  membersCount: number;
  plan: string;
  creditsRemaining: number;
  spentThisMonth: number;
  refreshedAt: string;
  overrides: {
    customMonthlyCredits?: number;
    customEnvironmentsAllowed?: number;
    customRunHistoryDays?: number;
    customConcurrency?: number;
    customRateLimitRpm?: number;
    isExemptFromBilling?: boolean;
    priority?: 'normal' | 'high' | 'vip';
    agreementNotes?: string;
    contractExpiresAt?: string;
  };
  projectsCount: number;
  testsCount: number;
  runsCount: number;
}

export const getAdminOrganizations = (): Promise<{ organizations: AdminOrganizationItem[] }> =>
  api.get<{ organizations: AdminOrganizationItem[] }>(Routes.adminOrganizations);

export const updateAdminOrganizationQuota = (
  orgId: string,
  body: { plan?: string; overrides?: AdminOrganizationItem['overrides'] },
): Promise<any> =>
  api.put<any>(Routes.adminOrganizationQuota(orgId), body);

export const adjustAdminOrganizationCredits = (
  orgId: string,
  body: { delta: number; reason: string },
): Promise<any> =>
  api.post<any>(Routes.adminOrganizationCredits(orgId), body);

// ------------------------------------------------------------------ billing

export const createBillingCheckout = (
  body: BillingCheckoutRequest,
): Promise<BillingCheckoutResponse> =>
  api.post<BillingCheckoutResponse>(
    (Routes as unknown as { billingCheckout?: string }).billingCheckout ?? '/v1/billing/checkout',
    body,
  );

export const getBillingSubscription = (): Promise<BillingSubscriptionResponse> =>
  api.get<BillingSubscriptionResponse>(
    (Routes as unknown as { billingSubscription?: string }).billingSubscription ?? '/v1/billing/subscription',
  );

// ------------------------------------------------------------------ test generation (BYOK & hosted)

export const generateTestPlan = (body: {
  projectId: string;
  source: string;
  hint?: string;
  provider?: string;
  apiKey?: string;
  baseUrl?: string;
  model?: string;
}): Promise<GeneratedPlan> =>
  api.post<GeneratedPlan>('/v1/tests/generate', body);

